import * as React from 'react';
import { useAuthnMe } from '../../AuthnMeProvider';
import { useOptionalNavTree } from '../../redesign/nav/useNavTree';
import ThemeToggle from '../ThemeToggle';
import { LogOutIcon, SettingsIcon, PhoneOutgoingIcon, ScrollTextIcon, UsersIcon, LayoutDashboardIcon, WorkflowIcon, ListTodoIcon, BookOpenIcon, BuildingIcon, PlayCircleIcon, CreditCardIcon, BarChart3Icon } from 'lucide-react';
import logoUrl from '../../svgs/new-logo.svg';
import './navbar.css';

export type NavContext = 'admin' | 'customer' | 'reseller' | 'config' | 'operate';

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  href: string;
  active?: boolean;
  newTab?: boolean;
}

interface NavBarProps {
  context: NavContext;
  customerId?: string;
  botId?: string;
  currentPage?: string;
}

function NavBar({ context, customerId, botId, currentPage }: NavBarProps) {
  // Inside the v2 shell (AppShell + Sidenav) the shell owns navigation; a legacy
  // page wrapped there must not stack its own bar on top. navbar.css forces
  // display:flex !important, so this has to be a render-time decision, not CSS.
  const inV2Shell = useOptionalNavTree() !== null;
  const { user, logout, isAuthenticated, hasEverLoggedIn } = useAuthnMe();

  // Build navigation items based on context
  const navItems: NavItem[] = React.useMemo(() => {
    switch (context) {
      case 'admin':
        return [
          { id: 'dashboard', label: 'Dashboard', icon: <LayoutDashboardIcon />, href: '/admin' },
          { id: 'customers', label: 'Customers', icon: <UsersIcon />, href: '/admin#customers' },
          { id: 'bots', label: 'Bots', icon: <SettingsIcon />, href: '/admin#bots' },
          { id: 'resellers', label: 'Resellers', icon: <BuildingIcon />, href: '/admin#resellers' },
          { id: 'billing', label: 'Billing', icon: <CreditCardIcon />, href: '/admin/billing' },
        ];
      case 'customer':
        const basePath = customerId ? `/customer/${customerId}` : '/customer';
        return [
          { id: 'bots', label: 'My Bots', icon: <SettingsIcon />, href: basePath },
          { id: 'workflows', label: 'Workflows', icon: <WorkflowIcon />, href: `${basePath}/workflows` },
          { id: 'tasks', label: 'Tasks', icon: <ListTodoIcon />, href: `${basePath}/tasks` },
          { id: 'usage', label: 'Usage', icon: <BarChart3Icon />, href: `${basePath}/usage` },
          { id: 'billing', label: 'Billing', icon: <CreditCardIcon />, href: `${basePath}/billing` },
        ];
      case 'reseller':
        return [
          { id: 'dashboard', label: 'Dashboard', icon: <LayoutDashboardIcon />, href: '/reseller' },
          { id: 'customers', label: 'Customers', icon: <UsersIcon />, href: '/reseller#customers' },
        ];
      case 'config':
        return [
          { id: 'try', label: 'Try Bot', icon: <PlayCircleIcon />, href: `/b/${botId}`, newTab: true },
          { id: 'config', label: 'Config', icon: <SettingsIcon />, href: `/b/${botId}/config` },
          { id: 'operate', label: 'Outbound', icon: <PhoneOutgoingIcon />, href: `/b/${botId}/operate` },
          { id: 'logs', label: 'Call Logs', icon: <ScrollTextIcon />, href: `/b/${botId}/call-logs` },
          { id: 'embed', label: 'Embed', icon: <BookOpenIcon />, href: `/b/${botId}/help` },
          { id: 'docs', label: 'API', icon: <BookOpenIcon />, href: `/b/${botId}/api-docs` },
        ];
      case 'operate':
        return [
          { id: 'try', label: 'Try Bot', icon: <PlayCircleIcon />, href: `/b/${botId}`, newTab: true },
          { id: 'config', label: 'Config', icon: <SettingsIcon />, href: `/b/${botId}/config` },
          { id: 'operate', label: 'Outbound', icon: <PhoneOutgoingIcon />, href: `/b/${botId}/operate` },
          { id: 'logs', label: 'Call Logs', icon: <ScrollTextIcon />, href: `/b/${botId}/call-logs` },
          { id: 'embed', label: 'Embed', icon: <BookOpenIcon />, href: `/b/${botId}/help` },
          { id: 'docs', label: 'API', icon: <BookOpenIcon />, href: `/b/${botId}/api-docs` },
        ];
      default:
        return [];
    }
  }, [context, customerId, botId]);

  // Mark current page as active
  const navItemsWithActive = navItems.map(item => ({
    ...item,
    active: currentPage === item.id,
  }));

  const contextLabel = {
    admin: 'Admin',
    customer: 'Dashboard',
    reseller: 'Reseller',
    config: 'Bot Config',
    operate: 'Outbound',
  }[context];

  if (inV2Shell) return null;

  return (
    <nav className="voxcore-navbar">
      {/* Left: Logo and Brand */}
      <div className="voxcore-navbar-left">
        <a href="/" className="voxcore-navbar-brand">
          <img src={logoUrl} className="voxcore-navbar-logo" aria-hidden="true" alt="" />
        </a>
        <div className="voxcore-navbar-divider" />
        <span className="voxcore-navbar-context">{contextLabel}</span>
      </div>

      {/* Center: Navigation Links */}
      <div className="voxcore-navbar-center">
        {navItemsWithActive.map((item) => (
          <a
            key={item.id}
            href={item.href}
            className={`voxcore-navbar-link ${item.active ? 'active' : ''}`}
            target={item.newTab ? '_blank' : undefined}
            rel={item.newTab ? 'noopener noreferrer' : undefined}
          >
            {item.icon}
            <span className="voxcore-navbar-link-text">{item.label}</span>
          </a>
        ))}
      </div>

      {/* Right: Theme Toggle + User Menu */}
      <div className="voxcore-navbar-right">
        <ThemeToggle />
        {isAuthenticated && user ? (
          <>
            <div className="voxcore-navbar-user">
              <div className="voxcore-navbar-avatar">
                {user.email ? user.email[0].toUpperCase() : 'U'}
              </div>
              <span className="voxcore-navbar-email">
                {user.email || 'User'}
              </span>
            </div>
            <button
              className="voxcore-navbar-logout"
              onClick={logout}
              title="Sign out"
            >
              <LogOutIcon />
            </button>
          </>
        ) : (
          <a href="/customer" className="voxcore-navbar-link">
            {hasEverLoggedIn ? 'Sign In' : 'Sign Up'}
          </a>
        )}
      </div>
    </nav>
  );
}

export { NavBar };
